import BaseFlyout from 'ext-ui/components/BaseFlyout/BaseFlyout'
import React, { useCallback, useEffect, useRef } from 'react'
import ReportingAgentChat, {
  type ReportingChatRunState,
} from 'src/agent/components/ReportingAgentChat/ReportingAgentChat'
import type { ReportingChartQuestionRequest } from 'src/agent/reporting/chartQuestion.types'
import type { ReportingAccessDeniedEvent } from 'src/agent/reporting/conversation/reportingConversation.types'
import { useReportingAgentConversation } from 'src/agent/reporting/conversation/useReportingAgentConversation'
import { useTranslate } from 'src/localization/Trns'
import styled from 'styled-components'

import style from './ReportingAgentFlyout.style'

export interface ReportingAgentFlyoutProps {
  actorId: string
  organizationId: string
  draftRequestId?: string
  conversationId?: string
  chartQuestionRequest?: ReportingChartQuestionRequest
  isOpen: boolean
  reportingEnabled?: boolean
  onClose: () => void
  onAccessDenied: (event: ReportingAccessDeniedEvent) => void
  onConversationAccepted: (conversationId: string) => void
}

const StyledBaseFlyout = styled(BaseFlyout)`
  ${style}
`

const toChatRunState = (
  status: ReturnType<typeof useReportingAgentConversation>['status'],
): ReportingChatRunState => {
  switch (status) {
    case 'submitting':
      return 'submitting'
    case 'running':
      return 'running'
    case 'refining':
      return 'refining'
    case 'error':
      return 'error'
    default:
      return 'idle'
  }
}

const ReportingAgentFlyout: React.FC<ReportingAgentFlyoutProps> = ({
  actorId,
  organizationId,
  draftRequestId,
  conversationId,
  chartQuestionRequest,
  isOpen,
  reportingEnabled = true,
  onClose,
  onAccessDenied,
  onConversationAccepted,
}) => {
  const translate = useTranslate()
  const conversation = useReportingAgentConversation({
    actorId,
    organizationId,
    draftRequestId,
    conversationId,
    isAuthenticated: Boolean(reportingEnabled && actorId && organizationId),
    onAccessDenied,
    onConversationAccepted,
  })
  const submittedChartQuestionIdsRef = useRef(new Set<string>())
  useEffect(() => {
    if (
      !isOpen ||
      !chartQuestionRequest ||
      !conversation.canSubmit ||
      submittedChartQuestionIdsRef.current.has(chartQuestionRequest.id)
    ) {
      return
    }
    submittedChartQuestionIdsRef.current.add(chartQuestionRequest.id)
    conversation.submit(
      chartQuestionRequest.question,
      chartQuestionRequest.context,
    )
  }, [
    chartQuestionRequest,
    conversation.canSubmit,
    conversation.submit,
    isOpen,
  ])
  const handleClose = useCallback(() => {
    if (
      conversation.hasUnsavedWork &&
      !window.confirm(translate('Discard this report?'))
    ) {
      return
    }
    onClose()
  }, [conversation.hasUnsavedWork, onClose, translate])

  return (
    <StyledBaseFlyout
      className="ReportingAgentFlyout"
      isOpen={isOpen}
      onClose={handleClose}
      aria-label={translate('Report assistant')}
      variant="focus-background"
    >
      <ReportingAgentChat
        chartQuestionContext={
          chartQuestionRequest?.context ?? conversation.restoredChartContext
        }
        messages={conversation.messages}
        artifactByRunId={conversation.artifactByRunId}
        onSubmit={conversation.submit}
        statusMessage={conversation.statusMessage}
        errorMessage={conversation.errorMessage}
        runState={toChatRunState(conversation.status)}
        onPinArtifact={conversation.pinArtifact}
        pinnedArtifactIds={conversation.pinnedArtifactIds}
        pinningArtifactId={conversation.pinningArtifactId}
        pinUnavailableArtifactIds={conversation.pinUnavailableArtifactIds}
        canPinArtifacts={conversation.canPinArtifacts}
        hasOlderMessages={conversation.hasOlderMessages}
        isLoadingOlderMessages={conversation.isLoadingOlderMessages}
        olderMessagesFailed={conversation.olderMessagesFailed}
        onLoadOlderMessages={conversation.loadOlderMessages}
        disabled={!conversation.canEdit}
        submitDisabled={!conversation.canSubmit}
      />
    </StyledBaseFlyout>
  )
}

export default ReportingAgentFlyout
